/**
 * AccessoryPalette — "Add SlatWall accessories": one tile per Trusscore
 * accessory (data/slatwallAccessories.ts). Shown in edit mode while a SlatWall
 * section (or one of its accessories) is selected; a click hangs that
 * accessory on the section at the first free spot. Each tile is a small
 * drawing of the thing, seen from the side, at its relative size.
 */

import React from 'react';
import { SLATWALL_ACCESSORIES, SlatWallAccessoryDef, SlatWallAccessoryKind } from '../../data/slatwallAccessories';
import { DESIGNER_STRINGS as S } from './strings';

export interface AccessoryPaletteProps {
  onAdd: (kind: SlatWallAccessoryKind) => void;
  style?: React.CSSProperties;
}

const AMBER = '#FEB100';

/** A side view of the accessory on the wall: the wall at the left, the thing reaching out to the right. */
const Glyph: React.FC<{ def: SlatWallAccessoryDef }> = ({ def }) => {
  const W = 44, H = 34;
  // Scale so the biggest accessory fills the tile; the rest are drawn to it.
  const scale = 16 / 1.1; // px per foot: a 13" basket depth ≈ 16 px
  const d = def.size.d * scale, h = def.size.h * scale;
  const wallX = 8, floorY = H - 4;
  const y0 = floorY - h;
  const stroke = '#e8e8ee';
  let body: React.ReactNode;
  switch (def.kind) {
    case 'hook-single':
      body = <path d={`M ${wallX} ${y0} v ${h} M ${wallX} ${y0 + h * 0.45} h ${d - 2} q 2 0 2 -2 v -3`} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" />;
      break;
    case 'hook-double':
      body = (
        <>
          <path d={`M ${wallX} ${y0} v ${h} M ${wallX} ${y0 + h * 0.45} h ${d - 2} q 2 0 2 -2 v -3`} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" />
          <path d={`M ${wallX + 2} ${y0 + h * 0.45 + 3} h ${d - 4} q 2 0 2 -2 v -2`} fill="none" stroke="#9d9da8" strokeWidth={1.5} strokeLinecap="round" />
        </>
      );
      break;
    case 'hook-bike':
      body = <path d={`M ${wallX} ${y0} v ${h} M ${wallX} ${y0 + h * 0.4} h ${d - 4} q 4 0 4 -4 v -${h * 0.3}`} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" />;
      break;
    case 'hook-hose':
      body = <path d={`M ${wallX} ${y0} v ${h} M ${wallX} ${y0 + h * 0.5} h ${d - 3} q 3 0 3 -3 v -3 M ${wallX} ${y0 + h * 0.5 + 4} h ${d - 3}`} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" />;
      break;
    case 'basket-18':
      body = (
        <>
          <path d={`M ${wallX} ${y0} v ${h}`} stroke={stroke} strokeWidth={2} strokeLinecap="round" />
          <path d={`M ${wallX} ${y0 + 2} h ${d} v ${h - 2} h ${-d}`} fill="rgba(255,255,255,0.08)" stroke={stroke} strokeWidth={1.5} />
          {[0.25, 0.5, 0.75].map((f) => <line key={f} x1={wallX + d * f} y1={y0 + 2} x2={wallX + d * f} y2={floorY} stroke="#9d9da8" strokeWidth={1} />)}
        </>
      );
      break;
    default: // shelf-24
      body = (
        <>
          <path d={`M ${wallX} ${y0} v ${h}`} stroke={stroke} strokeWidth={2} strokeLinecap="round" />
          <path d={`M ${wallX} ${floorY - 2} h ${d} M ${wallX} ${floorY - 2} l ${d * 0.6} ${-h * 0.8}`} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" />
          <line x1={wallX + d * 0.5} y1={floorY - 2} x2={wallX + d * 0.5} y2={y0 + 2} stroke="#9d9da8" strokeWidth={1} />
        </>
      );
  }
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" style={{ display: 'block' }}>
      {/* The slats */}
      <rect x={2} y={2} width={4} height={H - 4} rx={1} fill="rgba(255,255,255,0.18)" />
      {[0.2, 0.4, 0.6, 0.8].map((f) => <line key={f} x1={2} y1={2 + (H - 4) * f} x2={6} y2={2 + (H - 4) * f} stroke="#1a1a1e" strokeWidth={1} />)}
      {body}
    </svg>
  );
};

export const AccessoryPalette: React.FC<AccessoryPaletteProps> = ({ onAdd, style }) => (
  <div
    role="toolbar"
    aria-label={S.accessories.title}
    onPointerDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
    style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: AMBER, textAlign: 'center' }}>{S.accessories.title}</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
      {SLATWALL_ACCESSORIES.map((def) => (
        <button
          key={def.kind}
          type="button"
          title={`${def.label} (${def.sku}) — ${S.accessories.hint}`}
          aria-label={def.label}
          className="designer-quick-layout"
          onClick={() => onAdd(def.kind)}
          style={{
            width: 64, padding: '6px 2px 5px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
            color: '#e4e4ea', cursor: 'pointer',
          }}
        >
          <Glyph def={def} />
          <span style={{ fontSize: 10, lineHeight: 1.15, textAlign: 'center', whiteSpace: 'normal' }}>{def.label}</span>
        </button>
      ))}
    </div>
  </div>
);

export default AccessoryPalette;
